import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from './Sidebar';
import { PageContainer } from './PageContainer';
import { DataAgentSidebar } from '@/components/DataAgentSidebar';
import { Button } from '@/components/ui/button';
import { Sun, Moon, Menu, X, Bot } from 'lucide-react';
import { useEnvironment } from '@/lib/environment-context';
import { cn } from '@/lib/utils';
import { DataAgentBridgeProvider } from '@/lib/data-agent-bridge-context';

interface AppShellProps {
  children: React.ReactNode;
  maxWidth?: string | number;
}

export function AppShell({ children, maxWidth = '1400px' }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [agentPanel, setAgentPanel] = useState<'closed' | 'open'>(() => {
    try {
      const stored = localStorage.getItem('qa-judge.agent-panel-state');
      return stored === 'closed' ? 'closed' : 'open';
    } catch { return 'open'; }
  });
  const [agentWidth, setAgentWidth] = useState(() => {
    try {
      const stored = localStorage.getItem('qa-judge.agent-panel-width');
      return stored ? Math.max(360, Math.min(800, parseInt(stored, 10))) : 480;
    } catch { return 480; }
  });
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const { deploymentMode } = useEnvironment();
  const handleOpenAgent = useCallback(() => setAgentPanel('open'), []);

  useEffect(() => {
    try { localStorage.setItem('qa-judge.agent-panel-state', agentPanel); } catch {}
  }, [agentPanel]);

  useEffect(() => {
    try { localStorage.setItem('qa-judge.agent-panel-width', String(agentWidth)); } catch {}
  }, [agentWidth]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme');
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initial = (stored || (prefersDark ? 'dark' : 'light')) as 'light' | 'dark';
      setTheme(initial);
      document.documentElement.classList.toggle('dark', initial === 'dark');
    } catch {}
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem('theme', next);
    } catch {}
    document.documentElement.classList.toggle('dark', next === 'dark');
  };

  return (
    <DataAgentBridgeProvider onOpenAgent={handleOpenAgent}>
    <div className="flex min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onAgentToggle={() => setAgentPanel(prev => prev === 'open' ? 'closed' : 'open')} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 border-b border-border flex items-center justify-between px-4 lg:px-6 bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded',
                deploymentMode === 'dev'
                  ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                  : 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
              )}
            >
              {deploymentMode}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setAgentPanel(prev => prev === 'open' ? 'closed' : 'open')}
              aria-label="Toggle data agent"
              className={cn(agentPanel === 'open' && 'bg-primary/10 text-primary')}
            >
              <Bot className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <PageContainer maxWidth={maxWidth}>
            {children}
          </PageContainer>
        </main>
      </div>

      {/* Data Agent right sidebar — inline panel */}
      {agentPanel === 'open' && (
        <DataAgentSidebar
          width={agentWidth}
          onWidthChange={setAgentWidth}
          onClose={() => setAgentPanel('closed')}
        />
      )}
    </div>
    </DataAgentBridgeProvider>
  );
}

